import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useRef, useState } from "react";

import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import { type SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import {
  CHART_NOTES_STYLE_TAB_LIST_SX,
  ChartNotesStyleTabLabel
} from "components/chart-notes-style-tabs";
import { AMBIENT_SCRIBE_PAUSED_FEATURE } from "constants/feature-flags";
import MedicalScribeFeedbackDialog from "features/medical-scribe/components/medical-scribe-feedback-dialog.component";
import {
  AMBIENT_SCRIBE_STATUS_COMPLETED,
  AMBIENT_SCRIBE_STATUS_PAUSED,
  AMBIENT_SCRIBE_STATUS_PROCESSING,
  AMBIENT_SCRIBE_STATUS_RECORDING,
  RECORDING_STATE_IN_PROGRESS
} from "features/medical-scribe/medical-scribe-constants";
import { selectMedicalScribe } from "features/medical-scribe/medical-scribe.slice";
import type { MedicalScribe } from "features/medical-scribe/medical-scribes-section/medical-scribes.type";
import EditScribeTab from "features/medical-scribe/paused-edit";
import PausedScribeActionButtons from "features/medical-scribe/paused-edit/paused-scribe-action-buttons.component";
import { usePausedScribeActions } from "features/medical-scribe/paused-edit/use-paused-scribe-actions.hook";
import { AMBIENT_SCRIBE_REFETCH } from "hooks/use-event-bus/event-bus";
import { useEventBus } from "hooks/use-event-bus/use-event-bus.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import { useGetScribeAudioChunksQuery } from "services/ambient-scribe";
import type { AmbientScribeTemplateType } from "services/types/ambient-scribe-template.type";

import useLocalScribeAudioUrl from "./hooks/use-local-scribe-audio-url.hook";
import useScribeDetailQuery from "./hooks/use-scribe-detail-query.hook";
import useScribeDetailTabs, {
  type DetailTabOptions,
  type ScribeDetailTabs,
  getScribeDetailsKey
} from "./hooks/use-scribe-detail-tabs.hook";
import useScribeDetailsMode, {
  type DetailsMode,
  type EditBottomTab
} from "./hooks/use-scribe-details-mode.hook";
import useScribeGenerateActions from "./hooks/use-scribe-generate-actions.hook";
import useScribeGenerationLatches, {
  shouldPollScribe
} from "./hooks/use-scribe-generation-latches.hook";
import useSoapProvenance from "./hooks/use-soap-provenance.hook";
import ScribeDetailsHeader from "./scribe-details-header.component";
import ScribeDetailsViewPane from "./scribe-details-view-pane.component";
import SummaryGenerateErrorDialog from "./summary-generate-error-dialog.component";

/**
 * `DetailTabOptions` (`hideSalesCoachTab`, `initialTab`) reach the tabs only when these details own
 * them. A host that passes its own `tabs` (Insert Scribe) decides those itself and ignores both.
 */
type ViewDetailsProps = DetailTabOptions & {
  remoteMedicalScribeId: number | undefined;
  localMedicalScribe?: MedicalScribe;
  hideHeader?: boolean;
  detailsMode?: DetailsMode;
  onDetailsModeChange?: (mode: DetailsMode) => void;
  editBottomTab?: EditBottomTab;
  onEditBottomTabChange?: (tab: EditBottomTab) => void;
  /** Atomically sets edit sub-tab + Audio Recordings mode (used when parent owns both). */
  onEnterEdit?: (bottomTab: EditBottomTab) => void;
  onEditClose?: () => void;
  onEditFinalized?: () => void;
  /**
   * The detail tabs, from `useScribeDetailTabs`, when the parent needs the open tab too (Insert
   * Scribe enables and labels its button from it). Unset, the details keep their own.
   */
  tabs?: ScribeDetailTabs;
  /**
   * Restricts the Scribe tab's template picker to one template type (SN-19067). Unset by
   * default, which sends no `?type=` filter and lists every type.
   */
  templateType?: AmbientScribeTemplateType;
  /**
   * Audiology report this dialog was opened from (SN-19067). Together with
   * `templateType: "AudiologyReport"` it switches the Scribe tab over to the generated-note
   * flow — the summary lives on `GET /ai/ambient-scribes/notes/`, not on the scribe.
   */
  audiologyReportId?: number;
  sx?: SxProps;
};

export default function ViewScribeDetails(props: ViewDetailsProps) {
  if (props.tabs) return <ScribeDetailsBody {...props} tabs={props.tabs} />;
  return <ScribeDetailsWithOwnTabs {...props} />;
}

/**
 * Keeps the tab state here when no parent asked for it. It reads the same detail query as the
 * body (identical args, one cache entry); only the body's subscription polls.
 */
function ScribeDetailsWithOwnTabs(props: ViewDetailsProps) {
  const detail = useScribeDetailQuery({
    scribeId: props.remoteMedicalScribeId,
    templateType: props.templateType,
    audiologyReportId: props.audiologyReportId
  });
  const tabs = useScribeDetailTabs({
    remote: detail.remote,
    isLoading: detail.isLoading,
    isFetching: detail.isFetching,
    scribeKey: getScribeDetailsKey(
      props.remoteMedicalScribeId,
      props.localMedicalScribe?.local_id
    ),
    isAudiologyMode: detail.isAudiologyMode,
    isAvsEnabled: detail.isAvsEnabled,
    isConsultDebriefEnabled: detail.isConsultDebriefEnabled,
    hideSalesCoachTab: props.hideSalesCoachTab,
    initialTab: props.initialTab
  });
  return <ScribeDetailsBody {...props} tabs={tabs} />;
}

/** View Scribe | Audio Recordings, with the number of recorded sessions on the second. */
function ScribeDetailsModeTabs({
  scribeId,
  mode,
  onChange
}: {
  scribeId: number;
  mode: DetailsMode;
  onChange: (event: unknown, mode: DetailsMode) => void;
}) {
  const { data: audioChunks = [], isLoading } = useGetScribeAudioChunksQuery(
    { scribeId },
    { skip: !scribeId }
  );
  const audioRecordingsCount =
    !isLoading && audioChunks.length > 0 ? audioChunks.length : undefined;

  return (
    <TabContext value={mode}>
      <TabList
        onChange={onChange}
        sx={{ ...CHART_NOTES_STYLE_TAB_LIST_SX, mb: 1, flexShrink: 0 }}
      >
        <Tab
          value="view"
          label={
            <ChartNotesStyleTabLabel
              label="View Scribe"
              isActive={mode === "view"}
            />
          }
        />
        <Tab
          value="edit"
          label={
            <ChartNotesStyleTabLabel
              label="Audio Recordings"
              isActive={mode === "edit"}
              count={audioRecordingsCount}
            />
          }
        />
      </TabList>
    </TabContext>
  );
}

/** Resume / Complete for a recording on the server, worded for its current status. */
function ScribePausedActionButtons({
  status,
  pausedActions
}: {
  status: number | undefined;
  pausedActions: ReturnType<typeof usePausedScribeActions>;
}) {
  return (
    <PausedScribeActionButtons
      isPaused={status === AMBIENT_SCRIBE_STATUS_PAUSED}
      isCompleted={status === AMBIENT_SCRIBE_STATUS_COMPLETED}
      isRecordingStatus={status === AMBIENT_SCRIBE_STATUS_RECORDING}
      isProcessingStatus={status === AMBIENT_SCRIBE_STATUS_PROCESSING}
      canCompleteFromFailedOrCancelled={
        pausedActions.canCompleteFromFailedOrCancelled
      }
      isFailedOrCancelledAwaitingChunks={
        pausedActions.isFailedOrCancelledAwaitingChunks
      }
      isWidgetActive={pausedActions.isWidgetActive}
      isLocallyPausedWidget={pausedActions.isLocallyPausedWidget}
      isResuming={pausedActions.isResuming}
      isResumePleaseWait={pausedActions.isResumePleaseWait}
      isCompleting={pausedActions.isCompleting}
      onResume={pausedActions.handleResume}
      onComplete={pausedActions.handleComplete}
    />
  );
}

function ScribeDetailsBody({
  remoteMedicalScribeId,
  localMedicalScribe: local,
  hideHeader,
  detailsMode,
  onDetailsModeChange,
  editBottomTab: editBottomTabProp,
  onEditBottomTabChange,
  onEnterEdit,
  onEditClose,
  onEditFinalized,
  sx,
  tabs,
  templateType,
  audiologyReportId
}: ViewDetailsProps & { tabs: ScribeDetailTabs }) {
  const flags = useFlags();
  const isPausedFeatureEnabled = Boolean(
    flags[AMBIENT_SCRIBE_PAUSED_FEATURE] ?? false
  );
  const { userId } = useUserRoles();
  const { recordingState } = useAppSelector(selectMedicalScribe);
  const isRecordingInProgress = recordingState === RECORDING_STATE_IN_PROGRESS;

  const { mode, editBottomTab, handleEditFinalized, handleModeTabChange } =
    useScribeDetailsMode({
      scribeId: remoteMedicalScribeId,
      detailsMode,
      onDetailsModeChange,
      editBottomTab: editBottomTabProp,
      onEditBottomTabChange,
      onEnterEdit,
      onEditFinalized
    });
  // With a recording on the server, the paused-edit feature adds View Scribe | Audio Recordings
  // and the Resume / Complete buttons.
  const hasServerRecording = Boolean(remoteMedicalScribeId);
  const showViewEditTabs =
    isPausedFeatureEnabled && remoteMedicalScribeId != null;

  const [pollingInterval, setPollingInterval] = useState(0);
  const detail = useScribeDetailQuery(
    { scribeId: remoteMedicalScribeId, templateType, audiologyReportId },
    { pollingInterval, refetchOnMountOrArgChange: true }
  );
  const { remote } = detail;

  useEventBus(AMBIENT_SCRIBE_REFETCH, () => {
    if (remoteMedicalScribeId) detail.refetch();
  });

  const flushContextSaveRef = useRef<(() => Promise<void>) | null>(null);
  const pausedActions = usePausedScribeActions({
    scribeId: remoteMedicalScribeId,
    scribeStatus: remote?.status,
    patient: remote?.patient,
    chartNoteId: remote?.chart_note?.id,
    onClose: onEditClose,
    onFinalized: handleEditFinalized,
    onBeforeAction: () => flushContextSaveRef.current?.() ?? Promise.resolve()
  });

  const generation = useScribeGenerationLatches(remote);
  const { latches, setLatch } = generation;
  const shouldPoll = shouldPollScribe(
    latches,
    remote,
    pausedActions.isCompleting
  );
  useEffect(() => {
    setPollingInterval(shouldPoll ? 2500 : 0);
  }, [shouldPoll]);

  const actions = useScribeGenerateActions({
    scribeId: remoteMedicalScribeId,
    setLatch
  });

  const soapProvenance = useSoapProvenance({
    scribeId: remoteMedicalScribeId,
    enabled: detail.isTemplateTypesEnabled && !detail.isAudiologyMode,
    isGeneratingSoap: generation.isGeneratingSoap,
    soapNotes: remote?.soap_notes
  });
  const { markSoapRun } = soapProvenance;

  // GenerateScribeSection fires its own request; this holds the latch without waiting for a poll.
  const handleSoapGenerateStarted = useCallback(() => {
    setLatch("soap", true);
    markSoapRun();
  }, [setLatch, markSoapRun]);
  const handleAudiologySummaryGenerateStarted = useCallback(
    () => setLatch("audiologySummary", true),
    [setLatch]
  );
  const handleAfterVisitSummaryGenerateStarted = useCallback(
    () => setLatch("afterVisitSummary", true),
    [setLatch]
  );

  const localBlobUrl = useLocalScribeAudioUrl(local?.local_id);
  const audioUrl = remote?.recording ?? local?.recording_url ?? localBlobUrl;
  const duration = remote?.duration ?? local?.duration;

  const pausedActionButtons =
    isPausedFeatureEnabled && hasServerRecording ? (
      <ScribePausedActionButtons
        status={remote?.status}
        pausedActions={pausedActions}
      />
    ) : undefined;

  const header = hideHeader ? null : (
    <ScribeDetailsHeader
      remote={remote}
      local={local}
      remoteMedicalScribeId={remoteMedicalScribeId}
      duration={duration}
      isCompleting={pausedActions.isCompleting}
      pausedActionButtons={pausedActionButtons}
      audioUrl={audioUrl}
      isRecordingInProgress={isRecordingInProgress}
      hasModeTabs={showViewEditTabs}
    />
  );

  return (
    <Stack
      flex={6}
      sx={{
        boxSizing: "border-box",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        ...(showViewEditTabs ? {} : { mt: 1.75 }),
        ...sx
      }}
    >
      {showViewEditTabs && (
        <ScribeDetailsModeTabs
          scribeId={Number(remoteMedicalScribeId)}
          mode={mode}
          onChange={handleModeTabChange}
        />
      )}

      {mode === "edit" && showViewEditTabs ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <EditScribeTab
            key={`${remoteMedicalScribeId}-${editBottomTab}`}
            scribeId={Number(remoteMedicalScribeId)}
            initialBottomTab={editBottomTab}
            onClose={onEditClose ?? (() => {})}
            onFinalized={handleEditFinalized}
          />
        </Box>
      ) : (
        <ScribeDetailsViewPane
          detail={detail}
          tabs={tabs}
          header={header}
          duration={duration}
          generation={{
            isGeneratingSoap: generation.isGeneratingSoap,
            isGeneratingSummary: generation.isGeneratingSummary,
            isGeneratingTranscript: latches.transcript,
            isGeneratingAudiologySummary:
              generation.isGeneratingAudiologySummary,
            isGeneratingAfterVisitSummary:
              generation.isGeneratingAfterVisitSummary,
            isGeneratingDebrief: latches.debrief,
            debriefTimedOut: generation.debriefTimedOut,
            onGenerateSoap: actions.handleGenerateSoap,
            onGenerateSummary: actions.handleGenerateSummary,
            onGenerateTranscript: actions.handleGenerateTranscript,
            onSoapGenerateStarted: handleSoapGenerateStarted,
            onAudiologySummaryGenerateStarted:
              handleAudiologySummaryGenerateStarted,
            onAfterVisitSummaryGenerateStarted:
              handleAfterVisitSummaryGenerateStarted,
            onDebriefGenerateStarted: generation.startDebrief
          }}
          templateType={templateType}
          audiologyReportId={audiologyReportId}
          soapPrompt={soapProvenance.prompt}
          soapFailure={soapProvenance.soapFailure}
          userId={userId}
          flushContextSaveRef={flushContextSaveRef}
        />
      )}

      <MedicalScribeFeedbackDialog />

      <SummaryGenerateErrorDialog
        error={actions.summaryGenerateError}
        onClose={actions.clearSummaryGenerateError}
      />
    </Stack>
  );
}
